import { ACTION, BOOLEAN, NUMBER, OBJECT, STRING } from '../../component-validations';
import { makeArgumentsPrivate, pickArgs, sortMetadata } from './helpers';

const INITIAL = 'initial';
/**
 * Central registry of all shared prop configurations.
 * Import this single object instead of multiple named exports.
 */
const SHARED_ARGS = {
  /**
   * Common accessibility args
   */
  accessibility: {
    meta: {
      ariaLabel: {
        defaultValue: INITIAL,
        description: 'Accessible label for screen readers',
        htmlAttribute: 'aria-label',
        initialValue: '',
        type: STRING,
      },
      ariaDescribedBy: {
        defaultValue: INITIAL,
        description: 'ID of element that describes this component',
        htmlAttribute: 'aria-describedby',
        initialValue: '',
        type: STRING,
      },
      ariaLabelledBy: {
        defaultValue: INITIAL,
        description: 'ID of element that labels this component',
        htmlAttribute: 'aria-labelledby',
        initialValue: '',
        type: STRING,
      },
    },
  },

  /**
   * Common action handler args
   */
  actions: {
    meta: {
      close: {
        description: 'Action triggered to close/dismiss the component',
        type: ACTION,
      },
      onBeforeClose: {
        description: 'Action triggered before the component is closed',
        type: ACTION,
      },
      onClick: {
        description: 'Action triggered when the component is clicked',
        type: ACTION,
      },
      onClose: {
        description: 'Action triggered when the component is closed',
        type: ACTION,
      },
      onOpen: {
        description: 'Action triggered when the component is opened',
        type: ACTION,
      },
    },
  },

  /**
   * Common component state args
   */
  componentState: {
    meta: {
      animated: {
        defaultValue: false,
        description: 'Enables animations for state changes',
        type: BOOLEAN,
      },
      disabled: {
        defaultValue: false,
        description: 'Disables user interaction with the component',
        type: BOOLEAN,
      },
      id: {
        description: 'Unique identifier for the component instance',
        type: STRING,
        initialValue: '',
      },
      open: {
        defaultValue: false,
        description: 'Controls the open/closed state of the component',
        type: BOOLEAN,
      },
    },
  },

  /**
   * Common content args
   */
  content: {
    meta: {
      icon: {
        description: 'Icon to display',
        type: STRING,
      },
      iconOnly: {
        defaultValue: false,
        description: 'Component only displays an icon without text',
        type: BOOLEAN,
      },
      text: {
        description: 'Text content for the component',
        type: STRING,
      },
    },
  },

  /**
   * Common container args
   */
  container: {
    meta: {
      column: {
        defaultValue: false,
        description: 'Arrange children in a vertical column',
        forbids: [{ name: 'grid', reason: 'Cannot use column layout with grid layout' }],
        type: BOOLEAN,
      },
      grid: {
        defaultValue: false,
        description: 'Use CSS Grid layout for the container',
        forbids: [
          { name: 'column', reason: 'Cannot use column layout with grid layout' },
          { name: 'reverse', reason: 'Cannot reverse order when using grid layout' },
        ],
        type: BOOLEAN,
      },
      fitContent: {
        defaultValue: false,
        description: 'Fit container to content size (width and height)',
        initialValue: '',
        type: BOOLEAN,
      },
      fitContentHeight: {
        defaultValue: false,
        description: 'Fit container height to content',
        initialValue: '',
        type: BOOLEAN,
      },
      fitContentWidth: {
        defaultValue: false,
        description: 'Fit container width to content',
        initialValue: '',
        type: BOOLEAN,
      },
      reverse: {
        defaultValue: false,
        description: 'Reverse the order of child elements',
        forbids: [{ name: 'grid', reason: 'Cannot use column layout with grid layout' }],
        type: BOOLEAN,
      },
    },
  },

  /**
   * Props shared by both flex and grid layouts
   */
  flexAndGrid: {
    meta: {
      alignContent: {
        cssProperty: 'align-content',
        defaultValue: INITIAL,
        description: 'Aligns rows within the container when there is extra space',
        options: [
          'center',
          'flex-end',
          'flex-start',
          INITIAL,
          'space-around',
          'space-between',
          'stretch',
        ],
        type: STRING,
      },
      alignItems: {
        cssProperty: 'align-items',
        defaultValue: INITIAL,
        description: 'Aligns items along the cross axis',
        options: ['baseline', 'center', 'flex-end', 'flex-start', INITIAL, 'stretch'],
        type: STRING,
      },
      alignSelf: {
        cssProperty: 'align-self',
        defaultValue: INITIAL,
        description: 'Overrides align-items for individual items',
        options: ['auto', 'baseline', 'center', 'flex-end', 'flex-start', INITIAL, 'stretch'],
        type: STRING,
      },
      gap: {
        cssProperty: 'gap',
        defaultValue: INITIAL,
        description: 'Space between items (supports CSS units)',
        type: STRING,
      },
      justifyContent: {
        cssProperty: 'justify-content',
        defaultValue: INITIAL,
        description: 'Aligns items along the main axis',
        options: [
          'center',
          'flex-end',
          'flex-start',
          INITIAL,
          'space-around',
          'space-between',
          'space-evenly',
        ],
        type: STRING,
      },
    },
  },

  /**
   * Props specific to flex layouts only
   */
  flexOnly: {
    meta: {
      flex: {
        cssProperty: 'flex',
        defaultValue: INITIAL,
        description: 'Flex grow/shrink/basis shorthand',
        initialValue: '',
        type: STRING,
      },
      flexWrap: {
        cssProperty: 'flex-wrap',
        defaultValue: INITIAL,
        description: 'Whether items wrap to new lines',
        options: [INITIAL, 'nowrap', 'wrap', 'wrap-reverse'],
        type: STRING,
      },
      order: {
        cssProperty: 'order',
        defaultValue: INITIAL,
        description: 'Order of the flex item',
        initialValue: '',
        type: STRING,
      },
    },
  },

  /**
   * Grid convenience args (simplified API)
   */
  gridConvenience: {
    meta: {
      area: {
        cssProperty: 'grid-area',
        defaultValue: INITIAL,
        description: 'Grid area name (convenience for grid-area)',
        initialValue: '',
        type: STRING,
      },
      areas: {
        cssProperty: 'grid-template-areas',
        defaultValue: INITIAL,
        description: 'Grid template areas (convenience for grid-template-areas)',
        initialValue: '',
        type: STRING,
      },
      autoFlow: {
        cssProperty: 'grid-auto-flow',
        defaultValue: INITIAL,
        description: 'Grid auto flow direction (convenience for grid-auto-flow)',
        options: ['column', 'column dense', 'dense', INITIAL, 'row', 'row dense'],
        type: STRING,
      },
      columns: {
        cssProperty: 'grid-template-columns',
        defaultValue: INITIAL,
        description: 'Grid columns definition (convenience for grid-template-columns)',
        initialValue: '',
        type: STRING,
      },
      rows: {
        cssProperty: 'grid-template-rows',
        defaultValue: INITIAL,
        description: 'Grid rows definition (convenience for grid-template-rows)',
        initialValue: '',
        type: STRING,
      },
    },
  },

  /**
   * Grid-specific args (full CSS API)
   */
  gridOnly: {
    meta: {
      gridArea: {
        cssProperty: 'grid-area',
        defaultValue: INITIAL,
        description: 'Grid area shorthand',
        initialValue: '',
        type: STRING,
      },
      gridAutoColumns: {
        cssProperty: 'grid-auto-columns',
        defaultValue: INITIAL,
        description: 'Size of auto-generated grid columns',
        initialValue: '',
        type: STRING,
      },
      gridAutoFlow: {
        cssProperty: 'grid-auto-flow',
        defaultValue: INITIAL,
        description: 'How auto-placed items flow',
        options: ['column', 'column dense', 'dense', INITIAL, 'row', 'row dense'],
        type: STRING,
      },
      gridAutoRows: {
        cssProperty: 'grid-auto-rows',
        defaultValue: INITIAL,
        description: 'Size of auto-generated grid rows',
        initialValue: '',
        type: STRING,
      },
      gridColumn: {
        cssProperty: 'grid-column',
        defaultValue: INITIAL,
        description: 'Grid column position shorthand',
        initialValue: '',
        type: STRING,
      },
      gridRow: {
        cssProperty: 'grid-row',
        defaultValue: INITIAL,
        description: 'Grid row position shorthand',
        initialValue: '',
        type: STRING,
      },
      gridTemplateAreas: {
        cssProperty: 'grid-template-areas',
        defaultValue: INITIAL,
        description: 'Named grid areas',
        initialValue: '',
        type: STRING,
      },
      gridTemplateColumns: {
        cssProperty: 'grid-template-columns',
        defaultValue: INITIAL,
        description: 'Grid column track sizes',
        initialValue: '',
        type: STRING,
      },
      gridTemplateRows: {
        cssProperty: 'grid-template-rows',
        defaultValue: INITIAL,
        description: 'Grid row track sizes',
        initialValue: '',
        type: STRING,
      },
      justifyItems: {
        cssProperty: 'justify-items',
        defaultValue: INITIAL,
        description: 'Aligns grid items along the row axis',
        options: ['center', 'end', INITIAL, 'start', 'stretch'],
        type: STRING,
      },
      justifySelf: {
        cssProperty: 'justify-self',
        defaultValue: INITIAL,
        description: 'Aligns a grid item along the row axis',
        options: ['center', 'end', INITIAL, 'start', 'stretch'],
        type: STRING,
      },
    },
  },

  /**
   * Common menu args
   */
  menu: {
    meta: {
      anchorElement: {
        description: 'Element to position the menu relative to',
        type: OBJECT,
      },
      menuAnchorOffset: {
        description: 'Offset for menu positioning relative to anchor element',
        type: NUMBER,
      },
      menuFitContent: {
        defaultValue: false,
        description: 'Size menu to fit content',
        type: BOOLEAN,
      },
    },
  },

  /**
   * Common positioning args
   */
  positioning: {
    meta: {
      absolute: {
        cssProperty: 'position',
        defaultValue: false,
        description: 'Position absolute',
        type: BOOLEAN,
      },
      fixed: {
        cssProperty: 'position',
        defaultValue: false,
        description: 'Position fixed',
        type: BOOLEAN,
      },
    },
  },

  // Computed groups for convenience (lazily computed)
  get allLayout() {
    return {
      meta: {
        ...this.flexAndGrid.meta,
        ...this.flexOnly.meta,
        ...this.gridConvenience.meta,
        ...this.gridOnly.meta,
      },
    };
  },

  get flex() {
    return {
      meta: {
        ...this.flexAndGrid.meta,
        ...this.flexOnly.meta,
      },
    };
  },

  get grid() {
    return {
      meta: {
        ...this.flexAndGrid.meta,
        ...this.gridConvenience.meta,
        ...this.gridOnly.meta,
      },
    };
  },
};

export const ACCESSIBILITY_ARGS = SHARED_ARGS.accessibility;
export const ACTION_ARGS = SHARED_ARGS.actions;
export const ALL_LAYOUT_ARGS = SHARED_ARGS.allLayout;
export const COMPONENT_STATE_ARGS = SHARED_ARGS.componentState;
export const CONTAINER_ARGS = SHARED_ARGS.container;
export const CONTENT_ARGS = SHARED_ARGS.content;
export const FLEX_ARGS = SHARED_ARGS.flex;
export const GRID_ARGS = SHARED_ARGS.grid;
export const MENU_ARGS = SHARED_ARGS.menu;
export const POSITIONING_ARGS = SHARED_ARGS.positioning;

export default {
  ACCESSIBILITY_ARGS,
  ACTION_ARGS,
  ALL_LAYOUT_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  FLEX_ARGS,
  GRID_ARGS,
  MENU_ARGS,
  POSITIONING_ARGS,
  makeArgumentsPrivate,
  pickArgs,
  sortMetadata,
};
